import React, { useState, useEffect } from "react";
import {
	Box,
	Alert,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
	IconButton,
	Button,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { Edit as EditIcon, Check as CheckIcon, X as XIcon } from "lucide-react";
import ProfileCompletionSection from "../components/common/ProfileCompletionSection";
import NestedReporting from "../components/common/NestedReporting";
import TeacherGamesSummary from "../components/teacher/TeacherGamesSummary";
import TeacherSchedule from "../components/teacher/TeacherSchedule";
import TeacherOnboardingVideos from "../components/teacher/TeacherOnboardingVideos";
import FmeEmployeeDashboard from "../components/employee/FmeEmployeeDashboard";
import { ROLES } from "../config/supabase";
import UserNotifications from "../components/notifications/UserNotifications"; // New import
import { useGrade } from "../contexts/GradeContext";

// UserManagementSection component remains unchanged
const UserManagementSection = ({ supabase }) => {
	const [users, setUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [editForm, setEditForm] = useState({
		full_name: "",
		email: "",
		phone_number: "",
		role: "",
		school_id: "",
	});
	const [schools, setSchools] = useState([]);

	useEffect(() => {
		fetchUsers();
		fetchSchools();
	}, []);

	const fetchUsers = async () => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("status", "Pending Approval");
			if (error) throw error;
			setUsers(data || []);
		} catch (err) {
			console.error("Error fetching users:", err);
		}
	};

	const fetchSchools = async () => {
		try {
			const { data, error } = await supabase.from("schools").select("id, name");
			if (error) throw error;
			setSchools(data);
		} catch (err) {
			console.error("Error fetching schools:", err);
		}
	};

	const handleEditClick = (user) => {
		setSelectedUser(user);
		setEditForm({
			full_name: user.full_name || "",
			email: user.email || "",
			phone_number: user.phone_number || "",
			role: user.role || "",
			school_id: user.school_id || "",
		});
		setOpenDialog(true);
	};

	const handleSave = async () => {
		try {
			const { error } = await supabase
				.from("profiles")
				.update({
					full_name: editForm.full_name,
					email: editForm.email,
					phone_number: editForm.phone_number,
					role: editForm.role,
					school_id: editForm.school_id,
				})
				.eq("id", selectedUser.id);

			if (error) throw error;

			setSuccessMessage("User details updated successfully");
			await fetchUsers();
			setOpenDialog(false);

			// Clear success message after 3 seconds
			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating user:", err);
		}
	};

	const handleStatusChange = async (userId, newStatus) => {
		try {
			const updates = {
				status: newStatus,
				...(newStatus === "Active"
					? { approved_at: new Date().toISOString() }
					: {}),
				...(newStatus === "Inactive"
					? { deactivated_at: new Date().toISOString() }
					: {}),
			};

			const { error } = await supabase
				.from("profiles")
				.update(updates)
				.eq("id", userId);

			if (error) throw error;

			// Set appropriate success message
			setSuccessMessage(
				newStatus === "Active"
					? "User has been approved successfully"
					: "User has been deactivated successfully"
			);

			await fetchUsers();

			// Clear success message after 3 seconds
			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating user status:", err);
		}
	};

	// If no pending users, show a message instead of the table
	if (users.length === 0) {
		return (
			<Paper
				elevation={0}
				sx={{
					p: 2,
					mt: 3,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<Typography variant="h6" sx={{ mb: 2 }}>
					Pending Approvals
				</Typography>
				<Typography variant="body1" color="text.secondary">
					No users are pending approval at this time.
				</Typography>
			</Paper>
		);
	}

	return (
		<>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{
					p: 2,
					mt: 3,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<Typography variant="h6" sx={{ mb: 2 }}>
					Pending Approvals
				</Typography>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Role</TableCell>
								<TableCell>Phone</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{users.map((user) => (
								<TableRow key={user.id}>
									<TableCell>{user.full_name || "N/A"}</TableCell>
									<TableCell>{user.email}</TableCell>
									<TableCell>{user.role}</TableCell>
									<TableCell>{user.phone_number || "N/A"}</TableCell>
									<TableCell>{user.status}</TableCell>
									<TableCell>
										<IconButton
											size="small"
											onClick={() => handleEditClick(user)}
											sx={{ mr: 1 }}>
											<EditIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											color="success"
											onClick={() => handleStatusChange(user.id, "Active")}
											sx={{ mr: 1 }}>
											<CheckIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleStatusChange(user.id, "Inactive")}>
											<XIcon size={16} />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			<Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
				<DialogTitle>Edit User</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						label="Full Name"
						fullWidth
						value={editForm.full_name}
						onChange={(e) =>
							setEditForm({ ...editForm, full_name: e.target.value })
						}
					/>
					<TextField
						margin="dense"
						label="Email"
						fullWidth
						value={editForm.email}
						onChange={(e) =>
							setEditForm({ ...editForm, email: e.target.value })
						}
					/>
					<TextField
						margin="dense"
						label="Phone Number"
						fullWidth
						value={editForm.phone_number}
						onChange={(e) =>
							setEditForm({ ...editForm, phone_number: e.target.value })
						}
					/>
					<TextField
						margin="dense"
						label="Role"
						fullWidth
						select
						value={editForm.role}
						onChange={(e) =>
							setEditForm({ ...editForm, role: e.target.value })
						}>
						<MenuItem value="ROLE_SCHOOL_TEACHER">School Teacher</MenuItem>
						<MenuItem value="ROLE_FUNMATH_ADMIN">Funmath Admin</MenuItem>
						<MenuItem value="ROLE_FUNMIYO_EMPLOYEE">Funmath Expert</MenuItem>
						<MenuItem value="ROLE_SCHOOL_ADMIN">School Admin</MenuItem>
					</TextField>
					<TextField
						margin="dense"
						label="School"
						fullWidth
						select
						value={editForm.school_id}
						onChange={(e) =>
							setEditForm({ ...editForm, school_id: e.target.value })
						}>
						{schools.map((school) => (
							<MenuItem key={school.id} value={school.id}>
								{school.name}
							</MenuItem>
						))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const Dashboard = () => {
	const { user, supabase } = useAuth();
	const { selectedGrade } = useGrade();
	const [error, setError] = useState(null);
	const [userProfile, setUserProfile] = useState(null);
	const [onboardingComplete, setOnboardingComplete] = useState(false);

	useEffect(() => {
		const fetchUserProfile = async () => {
			try {
				const { data, error } = await supabase
					.from("profiles")
					.select("*")
					.eq("id", user.id)
					.single();

				if (error) {
					// Handle profile not found gracefully
					if (error.code === "PGRST116") {
						console.warn("Profile not found for user in Dashboard:", user.id);
						setUserProfile(null);
						setError("Profile not found. Please contact administrator.");
						return;
					}
					throw error;
				}
				
				setUserProfile(data);

				// Check if onboarding is complete for teachers
				if (data.role === "ROLE_SCHOOL_TEACHER") {
					checkOnboardingStatus(data.id);
				}
			} catch (err) {
				console.error("Error fetching user profile:", err);
				setError("Failed to load user profile");
				setUserProfile(null);
			}
		};

		const checkOnboardingStatus = async (userId) => {
			try {
				// Get total number of onboarding videos
				const { data: videosData, error: videosError } = await supabase
					.from("onboarding_videos")
					.select("id");

				if (videosError) throw videosError;
				const totalVideos = videosData.length;

				if (totalVideos === 0) {
					// If no videos exist, consider onboarding complete
					setOnboardingComplete(true);
					return;
				}

				// Get completed videos count
				const { data: progressData, error: progressError } = await supabase
					.from("teacher_onboarding_progress")
					.select("*")
					.eq("teacher_id", userId)
					.eq("status", "completed");

				if (progressError) throw progressError;

				// If all videos are completed, onboarding is complete
				setOnboardingComplete(progressData.length === totalVideos);
			} catch (err) {
				console.error("Error checking onboarding status:", err);
			}
		};

		fetchUserProfile();
	}, [user.id, supabase]);

	const renderStatusAlert = () => {
		if (!userProfile) return null;

		switch (userProfile.status) {
			case "Pending Approval":
				return (
					<Alert severity="warning" sx={{ mb: 3 }}>
						Your account is pending administrator approval. You'll have full
						access once your account is approved.
					</Alert>
				);
			case "Inactive":
				return (
					<Alert severity="error" sx={{ mb: 3 }}>
						Your account has been deactivated. Please contact support for
						assistance.
					</Alert>
				);
			default:
				return null;
		}
	};

	// Check if user is a teacher
	const isTeacher = userProfile?.role === ROLES.SCHOOL_TEACHER;
	const isFunmathAdmin = userProfile?.role === ROLES.FUNMATH_ADMIN;
	const isFunmiyoAdmin = userProfile?.role === ROLES.FUNMIYO_ADMIN;
	const isFunmiyoEmployee = userProfile?.role === ROLES.FUNMIYO_EMPLOYEE;
	const showOperationsDashboard =
		isFunmiyoEmployee || isFunmathAdmin || isFunmiyoAdmin;

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{error}
				</Alert>
			)}
			{renderStatusAlert()}

			{/* Profile Completion Section */}
			<ProfileCompletionSection user={user} supabase={supabase} />

			{/* User Notifications - Show for all active users */}
			{userProfile?.status === "Active" && (
				<Box sx={{ mb: 3 }}>
					<UserNotifications user={user} supabase={supabase} />
				</Box>
			)}

			{/* Show onboarding videos for teachers */}
			{isTeacher && userProfile?.status === "Active" && (
				<TeacherOnboardingVideos supabase={supabase} user={user} />
			)}

			{/* Only show regular teacher content if onboarding is complete or admin/employee */}
			{isTeacher && userProfile?.status === "Active" && (
				<>
					<TeacherSchedule
						supabase={supabase}
						user={user}
						selectedGrade={selectedGrade}
					/>
					<TeacherGamesSummary
						supabase={supabase}
						user={user}
						selectedGrade={selectedGrade}
					/>
				</>
			)}

			{isFunmathAdmin && userProfile?.status === "Active" && (
				<UserManagementSection supabase={supabase} />
			)}

			{showOperationsDashboard && userProfile?.status === "Active" && (
				<FmeEmployeeDashboard
					supabase={supabase}
					user={user}
					isAdminView={isFunmathAdmin || isFunmiyoAdmin}
					showFmePerformance={isFunmathAdmin || isFunmiyoAdmin}
				/>
			)}
		</Box>
	);
};

export default Dashboard;
